import React, { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Check, ShieldAlert, X, Loader2, Clock } from "lucide-react";

const money = (n) => {
  const [whole, decimal] = Number(n || 0).toFixed(2).split(".");
  return `R ${Number(whole).toLocaleString("en-US")}.${decimal}`;
};

const ago = (iso) => {
  if (!iso) return "";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m ago`;
};

/**
 * Session 31.7 — "Orders awaiting your approval".
 *
 * Shown on the Staff and Retail dashboards when the owner has switched on
 * "Have staff confirm order before departments". Customer (QR-scan) orders
 * are held here until a staff member taps Accept (sent to kitchen/bar) or
 * Decline (voided; the guest's order page says it was declined). Waiter-
 * placed orders never appear here.
 */
export const PendingApprovalsCard = ({ orders = [], busyId = null, onAccept, onDecline, testIdPrefix = "staff-approvals" }) => {
  // Session 31.8 — two-step decline: (1) optional free-text reason the
  // guest will see, (2) "Are you sure?" confirmation.
  const [declining, setDeclining] = useState(null);
  const [reason, setReason] = useState("");
  const [confirmStep, setConfirmStep] = useState(false);
  const closeDecline = () => { setDeclining(null); setReason(""); setConfirmStep(false); };
  if (!orders.length) return null;
  return (
    <section
      className="rounded-2xl border-2 border-sky-400/60 bg-sky-50/80 p-3 shadow-sm dark:border-sky-700 dark:bg-sky-950/30 sm:p-4"
      role="region"
      aria-label="Orders awaiting your approval"
      data-testid={`${testIdPrefix}-card`}
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sky-500 text-white">
            <ShieldAlert className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-sky-900 dark:text-sky-100">Orders awaiting your approval</p>
            <p className="text-xs text-sky-900/70 dark:text-sky-200/70">Customer orders are held here until you accept them. Declined orders never reach the kitchen or bar.</p>
          </div>
        </div>
        <Badge className="shrink-0 rounded-full bg-sky-500 text-white" data-testid={`${testIdPrefix}-count`}>{orders.length}</Badge>
      </div>

      <div className="space-y-2">
        {orders.map((o) => {
          const items = (o.items || []).filter((it) => it && !it.voided);
          const busy = busyId === o.id;
          return (
            <div key={o.id} className="rounded-xl border border-border bg-card p-3" data-testid={`${testIdPrefix}-order-${o.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold">
                    Table {o.table_number || "—"}
                    <span className="ml-2 text-xs font-normal text-muted-foreground">{o.order_number}</span>
                  </p>
                  <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{ago(o.created_at)}</span>
                    {o.customer_name ? <span>· {o.customer_name}</span> : null}
                    {o.customer_phone ? <span>· {o.customer_phone}</span> : null}
                  </p>
                </div>
                <p className="text-sm font-semibold tabular-nums" data-testid={`${testIdPrefix}-total-${o.id}`}>{money(o.total)}</p>
              </div>
              <ul className="mt-2 space-y-0.5 text-sm">
                {items.map((it, i) => (
                  <li key={it.id || i} className="flex justify-between gap-2">
                    <span className="min-w-0 truncate"><span className="font-medium tabular-nums">{it.quantity}×</span> {it.name}{it.note ? <span className="text-muted-foreground"> — {it.note}</span> : null}</span>
                    {it.department ? <span className="shrink-0 text-xs uppercase tracking-wide text-muted-foreground">{it.department}</span> : null}
                  </li>
                ))}
              </ul>
              <div className="mt-3 flex gap-2">
                <Button
                  type="button"
                  className="h-10 flex-1 rounded-xl bg-[hsl(var(--primary))] text-primary-foreground hover:bg-[hsl(var(--primary))]/90"
                  disabled={busy}
                  onClick={() => onAccept?.(o.id)}
                  data-testid={`${testIdPrefix}-accept-${o.id}`}
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  Accept
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="h-10 flex-1 rounded-xl border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                  disabled={busy}
                  onClick={() => setDeclining(o)}
                  data-testid={`${testIdPrefix}-decline-${o.id}`}
                >
                  <X className="h-4 w-4" />
                  Decline
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Step 1 — optional reason */}
      <Dialog open={!!declining && !confirmStep} onOpenChange={(open) => { if (!open) closeDecline(); }}>
        <DialogContent className="max-w-md rounded-[24px]" data-testid={`${testIdPrefix}-decline-dialog`}>
          <DialogHeader>
            <DialogTitle>Decline order {declining?.order_number}</DialogTitle>
            <DialogDescription>
              Table {declining?.table_number || "—"} · {money(declining?.total)}. You can tell the guest why — this shows on their order page. Optional.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor={`${testIdPrefix}-reason`}>Reason for the guest (optional)</Label>
            <Textarea
              id={`${testIdPrefix}-reason`}
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, 240))}
              placeholder="e.g. The kitchen is closed for tonight — sorry!"
              rows={3}
              className="rounded-xl"
              data-testid={`${testIdPrefix}-decline-reason`}
            />
            <p className="text-right text-[11px] text-muted-foreground">{reason.length}/240</p>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" variant="outline" className="rounded-xl" onClick={closeDecline} data-testid={`${testIdPrefix}-decline-cancel`}>Keep it</Button>
            <Button type="button" className="rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => setConfirmStep(true)} data-testid={`${testIdPrefix}-decline-continue`}>
              <X className="h-4 w-4" /> Decline order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Step 2 — are you sure? */}
      <AlertDialog open={!!declining && confirmStep} onOpenChange={(open) => { if (!open) closeDecline(); }}>
        <AlertDialogContent className="rounded-[24px]" data-testid={`${testIdPrefix}-decline-confirm-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to decline {declining?.order_number}?</AlertDialogTitle>
            <AlertDialogDescription>
              The order is voided and will not reach the kitchen or bar. The guest's order page will say it was declined
              {reason.trim() ? <> with your note: <span className="font-medium text-foreground">"{reason.trim()}"</span></> : " (no reason given)"}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl" onClick={(e) => { e.preventDefault(); setConfirmStep(false); }} data-testid={`${testIdPrefix}-decline-back`}>Go back</AlertDialogCancel>
            <AlertDialogAction
              className="rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => { const id = declining?.id; const r = reason.trim(); closeDecline(); if (id) onDecline?.(id, r || null); }}
              data-testid={`${testIdPrefix}-decline-confirm`}
            >
              Yes, decline
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};

export default PendingApprovalsCard;
